import { isAccessTokenExpired } from '@lib/auth0/access-token-claims';
import { withAccessTokenLock } from '@lib/auth0/access-token-lock';
import { ACCESS_TOKEN_ROUTE } from '@lib/auth0/access-token-route';

/**
 * Fetches a current access token from our session route.
 *
 * That route rotates the session ffh-backend issued us whenever the cached
 * access token has expired, so it succeeds for as long as the session lives,
 * which is months rather than the three days the Auth0 SDK session used to
 * allow (FFH-3019). A 401 here means the session itself is gone, and the
 * caller should fail rather than retry.
 */
export const fetchFreshAccessToken = async (): Promise<string> => {
  // Resolved against the page origin because the fetch polyfill in the test
  // environment (and any non-browser caller) rejects relative URLs.
  const response = await withAccessTokenLock(() =>
    fetch(new URL(ACCESS_TOKEN_ROUTE, window.location.origin).toString())
  );
  if (!response.ok) {
    throw new Error(`Failed to refresh the access token (${response.status})`);
  }

  const payload = (await response.json()) as { token?: unknown };
  if (typeof payload.token !== 'string' || payload.token.length === 0) {
    throw new Error(
      'Failed to refresh the access token (no token in the response)'
    );
  }

  return payload.token;
};

/**
 * Resolves the access token a realtime mint should present.
 *
 * Ably re-invokes its auth callback for the whole life of the connection,
 * which on the live points page outlives the access token the page mounted
 * with. Presenting that mounted token forever is FFH-2675: once it expired,
 * every renewal 401ed at `POST /ably/token` and the pitch silently stopped
 * updating. So a token that can be proven expired is replaced with a freshly
 * fetched one; a token that cannot be proven expired is presented as-is and
 * the server stays the judge.
 */
export const resolveRealtimeAccessToken = async (
  mountedToken: string,
  fetchFresh: () => Promise<string> = fetchFreshAccessToken
): Promise<string> => {
  if (!isAccessTokenExpired(mountedToken)) {
    return mountedToken;
  }
  return fetchFresh();
};
